import React from "react";
import Icon from "../../../components/AppIcon";

export const fieldClass = "w-full rounded-xl border border-[#dfccb5] bg-white px-3.5 py-2.5 text-sm text-[#3b1d0f] outline-none transition focus:border-[#d97706] focus:ring-2 focus:ring-[#f59e0b]/20 disabled:bg-[#f5eee5]";
export const primaryButton = "inline-flex items-center justify-center gap-2 rounded-xl bg-[#f59e0b] px-4 py-2.5 text-sm font-bold text-[#3b1d0f] transition hover:bg-[#e99208] disabled:cursor-not-allowed disabled:opacity-50";
export const secondaryButton = "inline-flex items-center justify-center gap-2 rounded-xl border border-[#dec7ad] bg-white px-4 py-2.5 text-sm font-semibold text-[#5c3a28] transition hover:border-[#f59e0b] hover:bg-[#fff8ed] disabled:cursor-not-allowed disabled:opacity-50";
export const dangerButton = "inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-bold text-red-700 transition hover:bg-red-100 disabled:opacity-50";

const normalizeOption = (option) => typeof option === "object"
  ? option
  : { value: option, label: String(option) };

export function Dropdown({ value, onChange, options = [], placeholder = "Selecione", disabled = false, ariaLabel }) {
  const rootRef = React.useRef(null);
  const listboxId = React.useId();
  const normalizedOptions = React.useMemo(() => options.map(normalizeOption), [options]);
  const selectedIndex = normalizedOptions.findIndex((option) => option.value === value);
  const selectedOption = selectedIndex >= 0 ? normalizedOptions[selectedIndex] : null;
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(Math.max(selectedIndex, 0));

  React.useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    return () => document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, [open]);

  React.useEffect(() => {
    if (open) setActiveIndex(Math.max(selectedIndex, 0));
  }, [open, selectedIndex]);

  const choose = (option) => {
    if (!option || option.disabled) return;
    onChange?.(option.value);
    setOpen(false);
  };

  const onKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const lastIndex = Math.max(normalizedOptions.length - 1, 0);
      if (event.key === "Home") setActiveIndex(0);
      else if (event.key === "End") setActiveIndex(lastIndex);
      else if (event.key === "ArrowDown") setActiveIndex((current) => Math.min(current + 1, lastIndex));
      else setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }
    if ((event.key === "Enter" || event.key === " ") && open) {
      event.preventDefault();
      choose(normalizedOptions[activeIndex]);
    }
  };

  return <div ref={rootRef} className="relative w-full">
    <button
      type="button"
      disabled={disabled}
      aria-label={ariaLabel}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls={listboxId}
      aria-activedescendant={open ? `${listboxId}-${activeIndex}` : undefined}
      onClick={() => setOpen((current) => !current)}
      onKeyDown={onKeyDown}
      className={`${fieldClass} flex items-center justify-between gap-3 text-left ${open ? "border-[#d97706] ring-2 ring-[#f59e0b]/20" : ""}`}
    >
      <span className={`min-w-0 flex-1 truncate ${selectedOption ? "text-[#3b1d0f]" : "text-[#8b7465]"}`}>{selectedOption?.label || placeholder}</span>
      <Icon name="ChevronDown" size={16} className={`shrink-0 text-[#9a5a16] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
    </button>
    {open ? <div className="absolute left-0 right-0 top-full z-[80] mt-1 overflow-hidden rounded-xl border border-[#d8b17e] bg-[#fffdf9] p-1 shadow-[0_18px_45px_rgba(63,33,23,0.22)]">
      <div id={listboxId} role="listbox" className="super-jhonny-dropdown-list max-h-60 overflow-y-auto py-1">
        {normalizedOptions.map((option, index) => {
          const selected = option.value === value;
          const active = index === activeIndex;
          return <button
            id={`${listboxId}-${index}`}
            key={`${String(option.value)}-${index}`}
            type="button"
            role="option"
            aria-selected={selected}
            disabled={option.disabled}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => choose(option)}
            className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition ${selected ? "bg-[#4a281b] font-bold text-white" : active ? "bg-[#fff0d8] text-[#4a281b]" : "text-[#5c3a28] hover:bg-[#fff0d8]"} disabled:cursor-not-allowed disabled:opacity-45`}
          >
            <span className="min-w-0 flex-1">{option.label}</span>
            {selected ? <Icon name="Check" size={15} className="shrink-0 text-[#f2a21b]" /> : null}
          </button>;
        })}
      </div>
    </div> : null}
  </div>;
}

export const formatDate = (value, includeTime = true) => {
  if (!value) return "Ainda não concluída";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("pt-BR", includeTime ? { dateStyle: "short", timeStyle: "short" } : { dateStyle: "short" }).format(date);
};
export const money = (value) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value || 0));
export const number = (value) => new Intl.NumberFormat("pt-BR").format(Number(value || 0));

const statusMap = {
  disconnected: ["eGestor não conectado", "bg-slate-100 text-slate-700", "Unplug"],
  importing: ["Importando", "bg-blue-100 text-blue-700", "LoaderCircle"],
  synced: ["Sincronizado", "bg-emerald-100 text-emerald-700", "CircleCheck"],
  error: ["Reconexão necessária", "bg-red-100 text-red-700", "CircleAlert"],
  attention: ["Atenção", "bg-amber-100 text-amber-700", "TriangleAlert"],
};

export function StatusBadge({ status }) {
  const [label, className, icon] = statusMap[status] || statusMap.attention;
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${className}`}><Icon name={icon} size={14} className={status === "importing" ? "animate-spin" : ""} />{label}</span>;
}

export function Metric({ label, value, helper, icon, tone = "default" }) {
  const toneClass = tone === "success" ? "border-emerald-200 bg-emerald-50" : tone === "warning" ? "border-amber-200 bg-amber-50" : tone === "danger" ? "border-red-200 bg-red-50" : "border-[#ead8c2] bg-white";
  return <article className={`rounded-2xl border p-4 shadow-sm ${toneClass}`}><div className="flex items-start justify-between gap-3"><div><div className="text-xs font-black uppercase tracking-wide text-[#8b6a52]">{label}</div><div className="mt-2 text-2xl font-black text-[#3b1d0f]">{value}</div>{helper ? <div className="mt-1 text-xs text-[#8b6a52]">{helper}</div> : null}</div><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#fff0d8] text-[#b45309]"><Icon name={icon} size={19} /></span></div></article>;
}

export function Field({ label, helper, children }) {
  return <label className="block"><span className="mb-1.5 block text-sm font-bold text-[#5c3a28]">{label}</span>{children}{helper ? <span className="mt-1.5 block text-xs leading-relaxed text-[#8b6a52]">{helper}</span> : null}</label>;
}

export function Modal({ title, subtitle, onClose, footer, children }) {
  return <div className="fixed inset-0 z-modal flex items-center justify-center bg-[#2f1b13]/65 p-4 backdrop-blur-sm" role="dialog" aria-modal="true"><div className="max-h-[92vh] w-full max-w-xl overflow-auto rounded-[26px] border border-[#ead8c2] bg-[#fffdf9] shadow-2xl"><header className="flex items-start justify-between gap-4 border-b border-[#ead8c2] px-5 py-4"><div><h2 className="text-xl font-black text-[#3b1d0f]">{title}</h2>{subtitle ? <p className="mt-1 text-sm text-[#8b6a52]">{subtitle}</p> : null}</div><button type="button" onClick={onClose} className="rounded-xl p-2 text-[#7a5c46] hover:bg-[#fff0d8]" aria-label="Fechar"><Icon name="X" size={19} /></button></header><div className="p-5">{children}</div>{footer ? <footer className="flex flex-wrap justify-end gap-2 border-t border-[#ead8c2] px-5 py-4">{footer}</footer> : null}</div></div>;
}
